import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { ButtonColor } from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import * as cssTable from '@delmonicos/shared-design-system-react/src/components/table/table.module.scss';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { InvoiceModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/invoices-service-lib/src/wrapper/outputs/invoice.model';
import { CellContext } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { downloadInvoicePdf } from '@app/feature/invoice/helper/invoice-pdf.helper';
import { useLazyCpmsBoGetInvoicePdfQuery } from '@service/cpms-bo-invoice.service';

export interface InvoiceListActionsCellProps
  extends CellContext<InvoiceModel, unknown> {}

export const InvoiceListActionsCell = (
  props: InvoiceListActionsCellProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { row } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canRead =
    userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.INVOICES,
      action: ActionsByResource.INVOICES.READ,
    });

  const canDownloadPdf =
    userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.INVOICES,
      action: ActionsByResource.INVOICES.GET_INVOICE_PDF,
    });

  // ACTIONS ///////////////////////////////////////////////////////////////////

  // Nothing to do

  // DOWNLOAD PDF ////////////////////////////////////////////////////////////////////

  const [lazyGetInvoicePdfQuery] = useLazyCpmsBoGetInvoicePdfQuery();

  const handleDownloadInvoicePdf = async () => {
    try {
      const invoice = row.original;

      const { isError, isSuccess, data, error } = await lazyGetInvoicePdfQuery(
        invoice.id,
      );

      if (isError) {
        throw new Error(JSON.stringify(error));
      }

      if (isSuccess) {
        await downloadInvoicePdf(invoice, data);
        toast.success(t('$.invoice-download-pdf.success'));
      }
    } catch (error: any) {
      toast.error(
        t('$.invoice-download-pdf.error', {
          error: error.toString(),
        }),
      );
    }
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack direction="row" alignItems="center" justifyContent="flex-end">
      {!canRead && <div />}
      {canRead && (
        <ButtonIcon
          color={ButtonColor.Ghost}
          icon="eye"
          onClick={() =>
            navigate(
              generatePath(APP_PATH.INVOICE.DETAILS, {
                id: row.original.id,
              }),
            )
          }
          className={cssTable.displayOnHover}
        />
      )}
      {canDownloadPdf && (
        <ButtonIcon
          icon="download-01"
          color={ButtonColor.Ghost}
          onClick={handleDownloadInvoicePdf}
          className={cssTable.displayOnHover}
        />
      )}
    </Stack>
  );
};
